import React, { useImperativeHandle, useRef, useCallback } from "react";
import { Editor as TinymceEditor } from "@tinymce/tinymce-react";
import { IEditorProps } from "./Editor.types";

/**
 * Fix legacy HTML that was previously processed by juice() — 
 * restores custom list classes and cleans up inlined styles.
 */
const fixLegacyListHtml = (html: string): string => {
  if (!html) return html;
  if (!html.includes("<ul")) return html;

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<body>${html}</body>`, "text/html");
  const allLists = doc.querySelectorAll("ul");

  const customClasses = ["custom-check", "custom-cross", "custom-star", "custom-arrow", "custom-diamond"];

  allLists.forEach((ul) => {
    const hasCustom = customClasses.some((c) => ul.classList.contains(c));
    if (hasCustom) return;

    const hasIconColor = ul.hasAttribute("data-icon-color");
    const items = ul.querySelectorAll(":scope > li");
    if (items.length === 0) return;

    let commonColor = "";
    let allSameColor = true;
    items.forEach((li) => {
      const liColor =
        (li as HTMLElement).style.color ||
        (li.querySelector("span") as HTMLElement)?.style?.color ||
        "";
      if (!commonColor && liColor) commonColor = liColor;
      if (liColor && commonColor && liColor !== commonColor) allSameColor = false;
    });

    if (hasIconColor || (allSameColor && commonColor)) {
      ul.classList.add("custom-arrow");
      if (!hasIconColor && commonColor) {
        ul.setAttribute("data-icon-color", commonColor);
      }
      (ul as HTMLElement).style.removeProperty("list-style");
      (ul as HTMLElement).style.removeProperty("list-style-type");
      (ul as HTMLElement).style.removeProperty("padding-left");
    }
  });

  return doc.body.innerHTML;
};

/**
 * Inject <style> tags into the editor doc for each ul[data-icon-color]
 */
const applyIconColors = (editor: any) => {
  const doc = editor.getDoc();
  if (!doc) return;
  // Remove old injected styles
  doc.querySelectorAll("style[data-icon-style]").forEach((s: any) => s.remove());
  // Apply fresh styles
  const lists = doc.querySelectorAll("ul[data-icon-color]");
  lists.forEach((list: any) => {
    const color = list.getAttribute("data-icon-color");
    if (!color) return;
    const listId = list.id || editor.dom.uniqueId("iconlist_");
    list.id = listId;
    const style = doc.createElement("style");
    style.setAttribute("data-icon-style", "true");
    style.textContent = `#${listId} li::before { color: ${color} !important; }`;
    doc.head.appendChild(style);
  });
};

export const Editor = React.forwardRef<
  { insertTextAtCursor: (text: string) => void },
  IEditorProps
>(({ content, onChange, height, isFromQuickReply = false, toolbar: toolbarProp }, ref) => {
  const editorRef = useRef<any>(null);
  const initialContentRef = useRef<string>(fixLegacyListHtml(content || ""));
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const handleEditorChange = useCallback((newContent: string) => {
    if (typeof newContent !== "string") return;
    onChangeRef.current(newContent);
  }, []);

  const insertTextAtCursor = useCallback((text: string) => {
    if (editorRef.current) {
      editorRef.current.execCommand("mceInsertContent", false, ` ${text} `);
    }
  }, []);

  useImperativeHandle(ref, () => ({ insertTextAtCursor }));

  const defaultToolbar = "emoticons | bold | italic | strikethrough | code | custom_add_variable";
  const finalToolbar = toolbarProp || defaultToolbar;

  return (
    <MemoizedTinyMCE
      initialValue={initialContentRef.current}
      height={height}
      toolbar={finalToolbar}
      isFromQuickReply={isFromQuickReply}
      onEditorChange={handleEditorChange}
      onInit={(editor: any) => { editorRef.current = editor; }}
    />
  );
});

// ─── Memoized inner component ─────────────────────────────────────────────────

interface IMemoizedTinyMCEProps {
  initialValue: string;
  height?: number | string;
  toolbar: string;
  isFromQuickReply: boolean;
  onEditorChange: (content: string) => void;
  onInit: (editor: any) => void;
}

const MemoizedTinyMCE = React.memo<IMemoizedTinyMCEProps>(
  ({ initialValue, height, toolbar, isFromQuickReply, onEditorChange, onInit }) => {
    const onChangeRef = useRef(onEditorChange);
    onChangeRef.current = onEditorChange;
    const onInitRef = useRef(onInit);
    onInitRef.current = onInit;

    const initConfig = useRef({
      height: height !== undefined ? height : 500,
      menubar: false,
      plugins: "lists advlist link image code emoticons strikethrough",
      toolbar: toolbar,
      toolbar_location: "bottom" as const,
      branding: false,
      content_style: `
        html { height: 100%; }
        body {
          font-family: Helvetica, Arial, sans-serif;
          font-size: 14px;
          min-height: 100%;
          margin: 0;
          padding: 12px 16px;
          background-color: #f9fafb;
          cursor: text;
        }
        ul.custom-check { list-style: none !important; padding-left: 4px !important; }
        ul.custom-check li { position: relative !important; padding-left: 22px !important; margin-bottom: 4px; }
        ul.custom-check li::before { content: '\\2713'; position: absolute; left: 0; color: #027302; font-weight: bold; }
        ul.custom-cross { list-style: none !important; padding-left: 4px !important; }
        ul.custom-cross li { position: relative !important; padding-left: 22px !important; margin-bottom: 4px; }
        ul.custom-cross li::before { content: '\\2717'; position: absolute; left: 0; color: #e46a76; font-weight: bold; }
        ul.custom-star { list-style: none !important; padding-left: 4px !important; }
        ul.custom-star li { position: relative !important; padding-left: 22px !important; margin-bottom: 4px; }
        ul.custom-star li::before { content: '\\2605'; position: absolute; left: 0; color: #dcb014; }
        ul.custom-arrow { list-style: none !important; padding-left: 4px !important; }
        ul.custom-arrow li { position: relative !important; padding-left: 22px !important; margin-bottom: 4px; }
        ul.custom-arrow li::before { content: '\\27A4'; position: absolute; left: 0; color: #1a97f5; }
        ul.custom-diamond { list-style: none !important; padding-left: 4px !important; }
        ul.custom-diamond li { position: relative !important; padding-left: 22px !important; margin-bottom: 4px; }
        ul.custom-diamond li::before { content: '\\25C6'; position: absolute; left: 0; color: #1a97f5; }
      `,
      entity_encoding: "raw" as const,
      valid_elements: "*[*]",
      valid_classes: "*",
      extended_valid_elements: "ul[class|style|data-icon-color|id]",
      force_br_newlines: false,
      force_p_newlines: true,
      advlist_bullet_styles: "default,circle,square",
      advlist_number_styles: "default,lower-alpha,lower-roman,upper-alpha,upper-roman",
      statusbar: true,
      setup: (editor: any) => {
        if (!isFromQuickReply) {
          editor.ui.registry.addButton("custom_add_variable", {
            text: "+ Add Variable",
            tooltip: "Add Variable",
            onAction: () => {
              document.dispatchEvent(new CustomEvent("addVariable"));
            },
          });
        }

        // Helper to apply/toggle a custom list class
        const toggleCustomList = (className: string) => {
          const node = editor.selection.getNode();
          const list = editor.dom.getParent(node, "ul");
          const allCustom = ["custom-check", "custom-cross", "custom-star", "custom-arrow", "custom-diamond"];

          if (list && editor.dom.hasClass(list, className)) {
            editor.dom.removeClass(list, className);
            return;
          }
          if (list) {
            allCustom.forEach((c) => editor.dom.removeClass(list, c));
            editor.dom.addClass(list, className);
            return;
          }
          editor.execCommand("InsertUnorderedList");
          const newList = editor.dom.getParent(editor.selection.getNode(), "ul");
          if (newList) editor.dom.addClass(newList, className);
        };

        const isInCustomList = (className: string) => {
          const node = editor.selection.getNode();
          const list = editor.dom.getParent(node, "ul");
          return !!list && editor.dom.hasClass(list, className);
        };

        editor.ui.registry.addToggleButton("custom_checklist", {
          icon: "checkmark",
          tooltip: "Check list (✓)",
          onAction: () => toggleCustomList("custom-check"),
          onSetup: (api: any) => {
            const h = () => api.setActive(isInCustomList("custom-check"));
            editor.on("NodeChange", h);
            return () => editor.off("NodeChange", h);
          },
        });

        editor.ui.registry.addToggleButton("custom_crosslist", {
          icon: "close",
          tooltip: "Cross list (✗)",
          onAction: () => toggleCustomList("custom-cross"),
          onSetup: (api: any) => {
            const h = () => api.setActive(isInCustomList("custom-cross"));
            editor.on("NodeChange", h);
            return () => editor.off("NodeChange", h);
          },
        });

        editor.ui.registry.addToggleButton("custom_starlist", {
          text: "⭐",
          tooltip: "Star list (★)",
          onAction: () => toggleCustomList("custom-star"),
          onSetup: (api: any) => {
            const h = () => api.setActive(isInCustomList("custom-star"));
            editor.on("NodeChange", h);
            return () => editor.off("NodeChange", h);
          },
        });

        editor.ui.registry.addToggleButton("custom_arrowlist", {
          text: "➤",
          tooltip: "Arrow list (➤)",
          onAction: () => toggleCustomList("custom-arrow"),
          onSetup: (api: any) => {
            const h = () => api.setActive(isInCustomList("custom-arrow"));
            editor.on("NodeChange", h);
            return () => editor.off("NodeChange", h);
          },
        });

        editor.ui.registry.addToggleButton("custom_diamondlist", {
          text: "◆",
          tooltip: "Diamond list (◆)",
          onAction: () => toggleCustomList("custom-diamond"),
          onSetup: (api: any) => {
            const h = () => api.setActive(isInCustomList("custom-diamond"));
            editor.on("NodeChange", h);
            return () => editor.off("NodeChange", h);
          },
        });

        // Icon color picker
        editor.ui.registry.addButton("custom_iconcolor", {
          icon: "color-picker",
          tooltip: "Change list icon color",
          onAction: () => {
            const node = editor.selection.getNode();
            const list = editor.dom.getParent(node, "ul");
            const allCustom = ["custom-check", "custom-cross", "custom-star", "custom-arrow", "custom-diamond"];
            const hasCustom = list && allCustom.some((c) => editor.dom.hasClass(list, c));

            if (!list || !hasCustom) {
              editor.notificationManager.open({ text: "Place cursor in a custom list first.", type: "info", timeout: 3000 });
              return;
            }

            editor.windowManager.open({
              title: "List Icon Color",
              body: { type: "panel", items: [{ type: "colorinput", name: "iconcolor", label: "Icon Color" }] },
              initialData: { iconcolor: list.getAttribute("data-icon-color") || "#000000" },
              buttons: [{ type: "cancel", text: "Cancel" }, { type: "submit", text: "Apply", primary: true }],
              onSubmit: (dialogApi: any) => {
                const color = dialogApi.getData().iconcolor;
                if (color && list) {
                  editor.dom.setAttrib(list, "data-icon-color", color);
                  applyIconColors(editor);
                }
                dialogApi.close();
              },
            });
          },
        });

        // Background color picker
        editor.ui.registry.addButton("custom_bgcolor", {
          icon: "highlight-bg-color",
          tooltip: "Change editor background color",
          onAction: () => {
            const doc = editor.getDoc();
            const currentBg = doc.body.style.backgroundColor || "#f9fafb";
            editor.windowManager.open({
              title: "Editor Background Color",
              body: { type: "panel", items: [{ type: "colorinput", name: "bgcolor", label: "Background Color" }] },
              initialData: { bgcolor: currentBg },
              buttons: [{ type: "cancel", text: "Cancel" }, { type: "submit", text: "Apply", primary: true }],
              onSubmit: (dialogApi: any) => {
                const color = dialogApi.getData().bgcolor;
                if (color) doc.body.style.backgroundColor = color;
                dialogApi.close();
              },
            });
          },
        });
      },
    });

    return (
      <TinymceEditor
        tinymceScriptSrc="/tinymce/tinymce.min.js"
        licenseKey="gpl"
        onInit={(_evt, editor) => {
          onInitRef.current(editor);
          // Restore icon colors on load
          setTimeout(() => applyIconColors(editor), 100);
        }}
        initialValue={initialValue}
        init={initConfig.current}
        onEditorChange={(content) => onChangeRef.current(content)}
      />
    );
  },
  // Never re-render
  () => true
);
